import { useCallback, useRef, useState } from 'react';

import type { ShopDetail } from '@/api/shop/shopDetailQuery';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import type { Professional } from '@/types/booking';
import { Logger } from '@/utils/logging';

import type { PasscodeRequestResult } from './types';

// Set<string> and Map<string, string> only exists in JavaScript runtime memory (the heap). It is not persisted to disk, AsyncStorage, MMKV, or any other durable storage. When the app is killed or restarted:

// The React Native JS engine (Hermes) shuts down and its entire memory is freed by the OS.
// On next launch, the JS bundle is re-evaluated from scratch, hitting const checkedBarberIds = new Set<string>() again — creating a brand new, empty Set.

// Module-level cache: Stores barber IDs that have been successfully validated
// This persists across hook re-mounts but clears on app restart
const checkedBarberIds = new Set<string>();

// Module-level cache: Stores validated passcodes by barber ID
// Used to restore passcode after successful validation
const validatedPasscodes = new Map<string, string>();

export interface UsePasscodeProtectorReturn {
  requestPasscode: (
    shop: ShopDetail,
    professional: Professional,
  ) => Promise<PasscodeRequestResult>;
  bottomSheetRef: React.RefObject<BottomSheetRef | null>;
  currentProfessional: Professional | null;
  isBottomSheetOpen: boolean;
  getValidatedPasscode: (barberId: string) => string | null;
  handleSuccess: (passcode: string) => void;
  handleCancel: () => void;
  handleDismiss: () => void;
}

export function usePasscodeProtector(): UsePasscodeProtectorReturn {
  const bottomSheetRef = useRef<BottomSheetRef>(null);
  const [currentProfessional, setCurrentProfessional] =
    useState<Professional | null>(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const resolveRef = useRef<((result: PasscodeRequestResult) => void) | null>(
    null,
  );

  const requestPasscode = useCallback(
    async (
      shop: ShopDetail,
      professional: Professional,
    ): Promise<PasscodeRequestResult> => {
      const barberId = professional.barber.id;

      if (!shop.bookingCodeEnabled || !professional.barber.requirePasscode) {
        Logger.console('Passcode not required for professional', { barberId });
        return {
          requiredPasscode: false,
          success: true,
          fromCache: false,
        };
      }

      Logger.console('Passcode required for professional', { barberId });

      if (checkedBarberIds.has(barberId)) {
        Logger.console('Passcode found in cache', { barberId });
        return {
          requiredPasscode: true,
          success: true,
          fromCache: true,
        };
      }

      // Step 3: Show bottom sheet and wait for result
      return new Promise<PasscodeRequestResult>((resolve) => {
        resolveRef.current = resolve;
        setCurrentProfessional(professional);
        setIsBottomSheetOpen(true);
      });
    },
    [],
  );

  const handleSuccess = useCallback(
    (passcode: string) => {
      if (currentProfessional) {
        const barberId = currentProfessional.barber.id;

        // Module-level cache: Stores barber IDs that have been successfully validated
        // This persists across hook re-mounts but clears on app restart
        checkedBarberIds.add(barberId);

        // Module-level cache: Stores validated passcodes by barber ID
        // Used to restore passcode after successful validation
        validatedPasscodes.set(barberId, passcode);

        Logger.console('Passcode validated successfully', { barberId });

        if (resolveRef.current) {
          resolveRef.current({
            requiredPasscode: true,
            success: true,
            fromCache: false,
          });
          resolveRef.current = null;
        }

        setIsBottomSheetOpen(false);
        bottomSheetRef.current?.close();
      }
    },
    [currentProfessional],
  );

  const handleCancel = useCallback(() => {
    Logger.console('Passcode validation cancelled', {
      barberId: currentProfessional?.barber.id,
    });

    if (resolveRef.current) {
      resolveRef.current({
        requiredPasscode: true,
        success: false,
        fromCache: false,
      });
      resolveRef.current = null;
    }

    setIsBottomSheetOpen(false);
    bottomSheetRef.current?.close();
  }, [currentProfessional]);

  const handleDismiss = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current({
        requiredPasscode: true,
        success: false,
        fromCache: false,
      });
      resolveRef.current = null;
    }
    setIsBottomSheetOpen(false);
    setCurrentProfessional(null);
  }, []);

  const getValidatedPasscode = useCallback(
    (barberId: string): string | null => {
      return validatedPasscodes.get(barberId) || null;
    },
    [],
  );

  return {
    requestPasscode,
    bottomSheetRef,
    currentProfessional,
    isBottomSheetOpen,
    getValidatedPasscode,
    handleSuccess,
    handleCancel,
    handleDismiss,
  };
}

export const clearPasscodeCache = () => {
  checkedBarberIds.clear();
  validatedPasscodes.clear();
};

export const addToPasscodeCache = (barberId: string, passcode: string) => {
  checkedBarberIds.add(barberId);
  validatedPasscodes.set(barberId, passcode);
};

export const isBarberPasscodeValidated = (barberId: string): boolean => {
  return checkedBarberIds.has(barberId);
};
